import { Card } from 'antd';
import { union, sumBy, groupBy } from 'lodash';
import { useCallback, useContext, useMemo } from 'react';
import { EVALUATION_PERIOD } from '../../enum/common.enum';
import { KPI_CODE } from '../../enum/toshiba.enum';
import { kpiValue } from '../../utils/farecast-util';
import ContentChart from '../base/content-chart';
import { useSnapshot } from '../../hooks/useSnapshot';
import { ToshibaContext } from '../../contexts/toshiba.context';
import { NAME_PATH, useTralitSetting } from '../../hooks/useTralitSetting';
import { AppContext } from '../../contexts/AppContext';
import { useNavigate } from 'react-router-dom';

type ChartProps = { dataProvider: any; loading: boolean };

const ChangeChart: React.FC<ChartProps> = (props) => {
  const { dataProvider, loading } = props;
  const navigate = useNavigate();
  const { color } = useContext(AppContext);
  const { kpiValue, kpiUnitLabel, drillFields, filter } = useContext(ToshibaContext);
  const { currentSnapshot, prefSnapshot, getMonthLabel } = useSnapshot();
  const { fontSize } = useTralitSetting({ namePath: NAME_PATH.PREFERENCE });
  const filterMethod = useCallback(
    () => (item) => {
      return drillFields.every((drillField) => drillField.value.includes(item[drillField.fieldKey]));
    },
    [drillFields, currentSnapshot, filter]
  );
  const gbOrderStatusCur = useMemo(() => {
    const res = groupBy(dataProvider.data.filter(filterMethod()), 'orderStatus');
    if (!res['調整']) {
      res['調整'] = null;
    }
    return res;
  }, [filterMethod, dataProvider]);
  const gbOrderStatusTar = useMemo(() => {
    const res = groupBy(dataProvider.target.filter(filterMethod()), 'orderStatus');
    if (!res['調整']) {
      res['調整'] = null;
    }
    return res;
  }, [filterMethod, dataProvider]);
  const toolTipFormatter = (params) => {
    return `${params
      .map((param) => {
        if (['placeholder', '总数'].includes(param.seriesName)) {
          return '';
        }
        if (param.name === `比較断面(${getMonthLabel(filter.targetSnapshot)})`) {
          if (!['差分', '前年'].includes(param.seriesName)) {
            return `<div><span>${param.marker}</span> <span>${param.seriesName}: </span><span>${kpiValue(
              KPI_CODE.SALES_AMOUNT,
              param.value,
              false,
              {
                [KPI_CODE.SALES_AMOUNT]: {
                  unitValue: 100000000,
                  unitLabel: '億円',
                  precision: 2,
                },
              }
            )}</span></div>`;
          }
        }
        if (param.name === '差分') {
          if (['diff', 'plan'].includes(param.seriesId)) {
            return `<div><span>${param.marker}</span> <span>${param.seriesName}: </span><span>${kpiValue(
              KPI_CODE.SALES_AMOUNT,
              param.value,
              false,
              {
                [KPI_CODE.SALES_AMOUNT]: {
                  unitValue: 100000000,
                  unitLabel: '億円',
                  precision: 2,
                },
              }
            )}</span></div>`;
          }
        }
        if (param.name === 'ポテンシャル差分') {
          if (['potentialDiff', 'plan'].includes(param.seriesId)) {
            return `<div><span>${param.marker}</span> <span>${param.seriesName}: </span><span>${kpiValue(
              KPI_CODE.SALES_AMOUNT,
              param.value,
              false,
              {
                [KPI_CODE.SALES_AMOUNT]: {
                  unitValue: 100000000,
                  unitLabel: '億円',
                  precision: 2,
                },
              }
            )}</span></div>`;
          }
        }
        if (param.name === `今月断面(${getMonthLabel(currentSnapshot)})`) {
          if (!['差分', '前年'].includes(param.seriesName)) {
            return `<div><span>${param.marker}</span> <span>${param.seriesName}: </span><span>${kpiValue(
              KPI_CODE.SALES_AMOUNT,
              param.value,
              false,
              {
                [KPI_CODE.SALES_AMOUNT]: {
                  unitValue: 100000000,
                  unitLabel: '億円',
                  precision: 2,
                },
              }
            )}</span></div>`;
          }
        }
        if (param.name === '前年同月') {
          if (['前年', '予算'].includes(param.seriesName)) {
            return `<div><span>${param.marker}</span> <span>${param.seriesName}: </span><span>${kpiValue(
              KPI_CODE.SALES_AMOUNT,
              param.value,
              false,
              {
                [KPI_CODE.SALES_AMOUNT]: {
                  unitValue: 100000000,
                  unitLabel: '億円',
                  precision: 2,
                },
              }
            )}</span></div>`;
          }
        }
        return '';
      })
      .join('')}`;
  };

  const chartOptions = useMemo(() => {
    const { data: orgData, target: orgTarget, prefYear: orgPrefYear, plan: orgPlan, potentialDiff: orgPotentialDiff } = dataProvider;
    const data = orgData.filter(filterMethod());
    const target = orgTarget.filter(filterMethod());
    const plan = orgPlan.filter(filterMethod());
    const prefYear = orgPrefYear.filter(filterMethod());
    const potentialDiff = orgPotentialDiff.filter(filterMethod());
    const map = {
      既受注: '①既受注',
      未受注: '②未受注',
      白地: '③白地',
      調整: '④調整',
    };

    const copiedGbOrderStatusCur = Object.keys(gbOrderStatusCur).reduce((prev, k) => {
      const mappedKey = map[k];
      if (gbOrderStatusCur[k]) {
        prev[mappedKey] = gbOrderStatusCur[k].map((s) => ({
          ...s,
          orderStatus: mappedKey,
        }));
      }
      return prev;
    }, {});
    const copiedGbOrderStatusTar = Object.keys(gbOrderStatusTar).reduce((prev, k) => {
      const mappedKey = map[k];
      if (gbOrderStatusTar[k]) {
        prev[mappedKey] = gbOrderStatusTar[k].map((s) => ({
          ...s,
          orderStatus: mappedKey,
        }));
      }
      return prev;
    }, {});
    const allOrderStatus = union([...Object.keys(copiedGbOrderStatusCur), ...Object.keys(copiedGbOrderStatusTar)]);
    const xAxisData = [
      `比較断面(${getMonthLabel(filter.targetSnapshot)})`,
      '差分',
      'ポテンシャル差分',
      `今月断面(${getMonthLabel(currentSnapshot)})`,
      '前年同月',
    ];

    const tarTotal = sumBy(target, KPI_CODE.SALES_AMOUNT);
    const curTotal = sumBy(data, KPI_CODE.SALES_AMOUNT);
    const planTotal = sumBy(plan, KPI_CODE.SALES_AMOUNT);
    const prefYearTotal = sumBy(prefYear, KPI_CODE.SALES_AMOUNT);
    const diff = curTotal - tarTotal;

    const tarPotentialValue = sumBy(potentialDiff, 'prev');
    const curPotentialValue = sumBy(potentialDiff, 'cur');
    const diffPotential = curPotentialValue - tarPotentialValue;

    const diffPlaceholder =
      curTotal + curPotentialValue > tarTotal + tarPotentialValue ? tarTotal + tarPotentialValue : curTotal + curPotentialValue;

    const beforeTotal = diffPlaceholder + Math.abs(diff);
    const potentialPlaceholder = beforeTotal + diffPotential;

    const options = {
      title: {
        value: '',
        styles: {
          fontSize: fontSize.chartTitle,
          margin: 0,
        },
      },
      tooltips: ['zoomIn', 'reset'],
      chartOptions: {
        textStyle: {
          fontFamily:
            "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,'Noto Sans',sans-serif,'Apple Color Emoji','Segoe UI Emoji','Segoe UI Symbol','Noto Color Emoji'",
        },
        animation: false,
        dataZoom: [
          {
            type: 'slider',
            yAxisIndex: 0,
            filterMode: 'none',
            showDetail: false,
          },
          {
            type: 'inside',
            yAxisIndex: 0,
            filterMode: 'none',
          },
        ],
        xAxis: [
          {
            type: 'category',
            nameGap: 10,
            axisLabel: {
              interval: 0,
              hideOverlap: false,
              fontSize: fontSize.chart,
              // formatter: (value) => dayjs(value).format(dimension.format),
            },
            nameTextStyle: {
              fontSize: fontSize.chart,
            },

            data: xAxisData,
          },
        ],
        yAxis: [
          {
            type: 'value',
            name: `値${kpiUnitLabel(KPI_CODE.SALES_AMOUNT)}`,
            nameGap: 10,
            axisLabel: {
              fontSize: fontSize.chart,
              formatter: (value) => kpiValue(KPI_CODE.SALES_AMOUNT, value),
            },
            nameTextStyle: {
              fontSize: fontSize.chart,
            },
            scale: true,
            // min: minY,
          },
        ],
        grid: {
          top: 45,
          right: 60,
          bottom: 40,
          left: 85,
        },
        legend: {
          data: ['予算', ...allOrderStatus, 'ポテンシャル', '差分', '前年'],
          selectedMode: false,
          textStyle: {
            fontSize: fontSize.chart,
          },
        },
        tooltip: {
          trigger: 'axis',
          appendToBody: true,
          axisPointer: {
            lineStyle: {
              width: 2,
            },
          },
          formatter: toolTipFormatter,
        },
        series: [
          {
            id: 'placeholder',
            name: 'placeholder',
            type: 'bar',
            stack: 'orderStatus',
            itemStyle: {
              borderColor: 'transparent',
              color: 'transparent',
            },
            emphasis: {
              itemStyle: {
                borderColor: 'transparent',
                color: 'transparent',
              },
            },
            xAxisIndex: 0,
            data: [0, diffPlaceholder, potentialPlaceholder, 0, 0],
            label: {
              show: false,
              fontSize: fontSize.chart,
              color: 'transparent',
            },
            barMaxWidth: 50,
          },
          {
            id: 'diff',
            name: '差分',
            type: 'bar',
            stack: 'orderStatus',
            itemStyle: {
              color: diff >= 0 ? color.successColor : color.errorColor,
            },
            data: [
              {
                value: 0,
              },
              {
                value: Math.abs(diff),
                show: true,
              },
              {
                value: 0,
              },
              {
                value: 0,
              },
              {
                value: 0,
              },
            ],
            label: {
              position: 'top',
              fontSize: fontSize.chart,
              formatter: (params) => {
                return params.data.show ? `${diff >= 0 ? '+' : '-'}${kpiValue(KPI_CODE.SALES_AMOUNT, params.value)}` : '';
              },
              color: diff >= 0 ? color.successColor : color.errorColor,
            },
            barMaxWidth: 50,
          },
          {
            id: 'potentialDiff',
            name: '差分',
            type: 'bar',
            stack: 'orderStatus',
            itemStyle: {
              color: diffPotential >= 0 ? color.successColor : color.errorColor,
            },
            data: [
              {
                value: 0,
              },
              {
                value: 0,
              },
              {
                value: Math.abs(diffPotential),
                show: true,
              },
              {
                value: 0,
              },
              {
                value: 0,
              },
            ],
            label: {
              position: 'top',
              fontSize: fontSize.chart,
              formatter: (params) => {
                return params.data.show ? `${diffPotential >= 0 ? '+' : '-'}${kpiValue(KPI_CODE.SALES_AMOUNT, params.value)}` : '';
              },
              color: diffPotential >= 0 ? color.successColor : color.errorColor,
            },
            barMaxWidth: 50,
          },
          ...allOrderStatus.map((orderStatus) => {
            const tarData = copiedGbOrderStatusTar[orderStatus];
            const curData = copiedGbOrderStatusCur[orderStatus];
            const tarValue = sumBy(tarData, KPI_CODE.SALES_AMOUNT);
            const curValue = sumBy(curData, KPI_CODE.SALES_AMOUNT);
            return {
              name: orderStatus,
              type: 'bar',
              stack: 'orderStatus',
              data: [
                {
                  value: tarValue,
                },
                {
                  value: 0,
                },
                {
                  value: 0,
                },
                {
                  value: curValue,
                },
                {
                  value: 0,
                },
              ],
              label: {
                position: 'top',
                fontSize: fontSize.chart,
                formatter: (params) => (params.data.show ? kpiValue(KPI_CODE.SALES_AMOUNT, params.value) : ''),
              },
              barMaxWidth: 50,
            };
          }),
          {
            name: 'ポテンシャル',
            type: 'bar',
            stack: 'orderStatus',
            data: [
              {
                value: tarPotentialValue,
              },
              {
                value: 0,
              },
              {
                value: 0,
              },
              {
                value: curPotentialValue,
              },
              {
                value: 0,
              },
            ],
            label: {
              position: 'top',
              fontSize: fontSize.chart,
              formatter: (params) => (params.data.show ? kpiValue(KPI_CODE.SALES_AMOUNT, params.value) : ''),
            },
            barMaxWidth: 50,
          },
          {
            name: '前年',
            type: 'bar',
            stack: 'orderStatus',
            data: [
              {
                value: 0,
              },
              {
                value: 0,
              },
              {
                value: 0,
              },
              {
                value: 0,
              },
              {
                value: prefYearTotal,
                show: true,
              },
            ],
            label: {
              position: 'top',
              fontSize: fontSize.chart,
              formatter: (params) => (params.data.show ? kpiValue(KPI_CODE.SALES_AMOUNT, params.value) : ''),
            },
            barMaxWidth: 50,
          },
          {
            id: 'plan',
            name: '予算',
            type: 'line',
            symbol: 'none',
            data: [planTotal, planTotal, planTotal, planTotal, planTotal],
            silent: true,
            itemStyle: {
              color: '#ff8331',
              borderType: 'dotted',
            },
            lineStyle: {
              type: 'dashed',
            },
            label: {
              show: false,
            },
            z: 0,
          },
          {
            name: '总数',
            type: 'bar',
            stack: 'orderStatus',
            data: [{ value: 1, show: tarTotal }, { value: 0 }, { value: 0 }, { value: 1, show: curTotal }, { value: 0 }],
            label: {
              show: true,
              position: 'top',
              fontSize: fontSize.chart,
              formatter: (params) => {
                return params.data.show ? kpiValue(KPI_CODE.SALES_AMOUNT, params.data.show) : '';
              },
            },
            itemStyle: {
              color: 'transparent', // 使其不可见
            },
            emphasis: {
              itemStyle: {
                color: 'transparent', // 鼠标悬停时也透明
              },
            },
            silent: true, // 禁用鼠标交互
          },
        ],
      },
      height: document.body.clientHeight / 3,
    };
    return options;
  }, [dataProvider, filter, fontSize, gbOrderStatusCur, gbOrderStatusTar]);
  const seriesClickHandler = (e) => {
    if (drillFields.length > 2) {
      return;
    }
    if (e.seriesId === 'diff') {
      navigate(`/toshiba/achieve-diff-analysis?evaluationPeriod=${EVALUATION_PERIOD.FORECAST_ONLY}`);
    }
  };
  return (
    <Card styles={{ body: { minHeight: document.body.clientHeight / 3 + 20, padding: 0 } }}>
      <ContentChart
        contentList={[]}
        {...chartOptions}
        showSeriesLabel
        mouseWheelZoomLock={false}
        loading={loading}
        tooltips={['zoomIn']}
        isBank={!dataProvider || !dataProvider.data || dataProvider.data.length === 0}
        onClick={seriesClickHandler}
      />
    </Card>
  );
};

export default ChangeChart;
